// src/components/useSiteData.js
//
// The site's own settings on the `site/photos` document — the background
// slideshow's photos and timing, and how fast the product cards move through
// their photos — for any part of a page that needs them.
//
// They are only known once the document has been read, so this is null until
// then; a returning visitor's browser remembers them, so for most visits they
// are known from the first paint.
import { useEffect, useState } from 'react';
import { getSitePhotos } from '../db';
import { onSitePhotosChange, rememberedSitePhotos } from '../photos';

// One read of the document per page load, shared by everything on it.
let request = null;

/** For tests: let the next use read the document again. */
export const forgetSitePhotoRead = () => {
  request = null;
};

/**
 * The `site/photos` document's fields, or null while they are not yet known.
 * Read once per page load, and kept up to date if the dashboard saves.
 */
export default function useSiteData() {
  const [data, setData] = useState(() => rememberedSitePhotos());

  useEffect(() => {
    let active = true;
    const stop = onSitePhotosChange(() => setData(rememberedSitePhotos()));
    if (!request) {
      // Through a promise, so a read that throws before it even starts is
      // caught like one that fails on the way.
      request = Promise.resolve()
        .then(() => getSitePhotos())
        .catch((err) => {
          console.error('Error loading the site photos', err);
          request = null;
          return null;
        });
    }
    request.then(() => {
      // getSitePhotos has remembered what it read, which the listener above
      // picks up. If the read failed with nothing remembered, the built-in
      // photo is better than an empty band.
      if (active) setData((current) => current || rememberedSitePhotos() || {});
    });
    return () => {
      active = false;
      stop();
    };
  }, []);

  return data;
}
